import { Injectable } from '@angular/core';
import { ThemeBase } from '@libs/themes';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SliderService {
  public readonly current$: Observable<number>;
  public readonly max$: Observable<number>;
  public readonly hasPrevious$: Observable<boolean>;
  public readonly hasNext$: Observable<boolean>;

  private current: number = 0;
  private max: number = 0;

  private topLevelMenuElement!: HTMLElement;
  private topLevelLinkElements!: HTMLElement[];

  public constructor() {
    this.current$ = new BehaviorSubject(this.current);
    this.max$ = new BehaviorSubject(this.max);

    this.hasPrevious$ = this.current$.pipe(
      map((value: number): boolean => value > 0),
    );

    this.hasNext$ = combineLatest(
      [this.current$, this.max$],
      (current: number, max: number): boolean => current < max,
    );
  }

  public initialize(navElement: HTMLElement): void {
    this.topLevelMenuElement = navElement.querySelector(
      '.nav-level-1',
    ) as HTMLElement;
    this.topLevelLinkElements = Array.from(
      this.topLevelMenuElement.querySelectorAll(':scope > li > a'),
    );

    this.updateSize();
  }

  public updateSize(): void {
    const hostWidth: number =
      this.topLevelMenuElement.getBoundingClientRect().width;
    const MobileWidth: number = Number.parseInt(
      ThemeBase.Screens.sm.replace('px', ''),
    );

    if (hostWidth < MobileWidth) {
      this.setSlideStyle(0);

      return;
    }

    for (
      let index: number = 0, width: number = 0;
      index < this.topLevelLinkElements.length;
      index++
    ) {
      const elementWidth: number =
        this.topLevelLinkElements[index].getBoundingClientRect().width;

      if (width + elementWidth > hostWidth) {
        break;
      }

      this.max = this.topLevelLinkElements.length - index - 1;

      width += elementWidth;
    }

    this.slideBy(0);
  }

  public slideBy(amount: number): void {
    this.current += amount;

    this.normalizeCurrent();
    this.updateObservables();

    this.setSlideStyle(this.getCurrentScrolledWidth());
  }

  private updateObservables(): void {
    (this.current$ as BehaviorSubject<number>).next(this.current);
    (this.max$ as BehaviorSubject<number>).next(this.max);
  }

  private normalizeCurrent(): void {
    if (this.current < 0) {
      this.current = 0;
    } else if (this.current > this.max) {
      this.current = this.max;
    }
  }

  private getCurrentScrolledWidth(): number {
    return this.topLevelLinkElements
      .slice(0, this.current)
      .reduce(
        (width: number, element: HTMLElement) =>
          width + element.getBoundingClientRect().width,
        0,
      );
  }

  private setSlideStyle(amount: number): void {
    this.topLevelLinkElements.forEach((element: HTMLElement): void => {
      element.style.transform = amount > 0 ? `translateX(-${amount}px)` : '';
    });
  }
}
